import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from '../../app.service';
import { CustomDataSource } from '../../utils/custom-data-source';
import { FolderModel } from 'src/app/shared/folder.model';
import { UtilsService } from 'src/app/shared/utils.service';
import { MatDialog } from '@angular/material/dialog';
import { ApiRequestService } from '../../shared/api-request.service';
import { saveAs } from 'file-saver';
import { AccountDocsLayoutComponent } from '../../account/account-docs/account-docs-layout/account-docs-layout.component';
import { TemplateDocsViewComponent } from '../../template-docs/template-docs-view/template-docs-view.component';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-employees-docs',
  templateUrl: './employees-docs.component.html',
  styleUrls: ['./employees-docs.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EmployeesDocsComponent implements OnInit, AfterViewInit {
  // columns to show and the data source
  displayedColumns: string[] = [
    'id',
    'name',
    'folder_type',
    'expiry',
    'created_at',
    'actions'
  ];
  dataSource = new EmployeeDocsDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate permission lookups on every change detection cycle.
  canView: boolean = false;
  canCreate: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public app: AppService,
    private api: ApiRequestService,
    private utils: UtilsService,
    private dialog: MatDialog,
    public rbac: RBACService
  ) {}

  // Expose RBACService to template
  protected readonly RBACService = RBACService;

  ngOnInit() {
    // Resolve the RBAC permission flags first so they are always set, even when
    // one of the access guards below short circuits the rest of this method.
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // Check if user has employees:view permission first
    const hasEmployeesView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );

    if (!hasEmployeesView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access employees.'
      );
      this.router.navigate(['/employees']);
      return;
    }

    // Check if user has ANY permission for employee documents (view OR create OR edit OR delete)
    const hasAnyDocumentPermission = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      false
    ); // false = OR logic - user needs ANY of these permissions

    if (!hasAnyDocumentPermission) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access employee training documents.'
      );
      this.router.navigate(['/employees']);
      return;
    }

    this.dataSource.user_id = Number(
      this.route.parent.snapshot.params['user_id']
    );

    if (this.dataSource.user_id) {
      // Only load data if user has view permission
      const hasViewPermission = this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );

      if (hasViewPermission) {
        this.dataSource.getData();
      }
    } else {
      this.router.navigate(['employees']);
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC columns by its corresponding date fields.
      if (this.sort.active == 'expiry_UTC') {
        this.dataSource.sort_by = 'expiry';
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onView(folder: FolderModel) {
    this.dialog
      .open(TemplateDocsViewComponent, {
        width: '750px',
        data: {
          folder: folder,
          is_training_doc: true,
          permissions: {
            feature: RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            canCreate: false,
            canEdit: false,
            canDelete: false
          }
        }
      })
      .afterClosed()
      .subscribe((result) => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData(true);
      });
  }

  onEdit(folder: FolderModel) {
    // Check permissions - requires employees:view + employees-training-competencies:view + employees-training-competencies:edit
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit employee training documents.'
      );
      return;
    }

    this.openDialog(folder);
  }

  onRemove(id: number) {
    // Check permissions - requires employees:view + employees-training-competencies:view + employees-training-competencies:delete
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete employee training documents.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Document',
      'Are you sure you want to remove this document?',
      () => {
        this.api
          .makeRequest('delete', 'v1/folders/' + id)
          .then((response: any) => {
            this.utils.showToast('The document was removed');
            this.dataSource.selection.deselect(id);
            this.dataSource.getData();
          });
      }
    );
  }

  onAdd() {
    // Check permissions - requires employees:view + employees-training-competencies:create
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create employee training documents.'
      );
      return;
    }

    this.openDialog({ user_id: this.dataSource.user_id } as FolderModel);
  }

  private openDialog(folder: FolderModel) {
    this.dialog
      .open(AccountDocsLayoutComponent, {
        width: '1200px',
        data: {
          folder: folder,
          is_training_doc: true,
          show_document_types_list_for: 'user',
          show_document_visibility: true,
          permissions: {
            feature: RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            canCreate: this.rbac.userCanAccess(
              [
                RBACService.getPermission(
                  RBACService.RBAC_F.F_EMPLOYEES,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
                  RBACService.RBAC_A.A_CREATE
                )
              ],
              true
            ),
            canEdit: this.rbac.userCanAccess(
              [
                RBACService.getPermission(
                  RBACService.RBAC_F.F_EMPLOYEES,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
                  RBACService.RBAC_A.A_EDIT
                )
              ],
              true
            ),
            canDelete: this.rbac.userCanAccess(
              [
                RBACService.getPermission(
                  RBACService.RBAC_F.F_EMPLOYEES,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
                  RBACService.RBAC_A.A_DELETE
                )
              ],
              true
            )
          }
        }
      })
      .afterClosed()
      .subscribe((result) => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      });
  }

  onExport(type: string = 'csv') {
    // Get the current date object
    const date = new Date();
    const user_id = this.dataSource.user_id;

    this.api
      .makeDownloadRequest(
        `v2/employees-folders/export/${type}/${user_id}`,
        {},
        {
          eager_load: true
        }
      )
      .then((file) => {
        saveAs(
          file,
          `${this.utils.getLangTerm(
            'employees.singular',
            'Employee'
          )} Training Documents - ${user_id} - ${
            this.app.account.name
          } - ${date.getFullYear()}${date.getMonth() +
            1}${date.getDate()}.${type}`
        );
      });
  }
}

/**
 * the data source class should contain everything needed to get data for the feature.
 * this only applies to lists. forms will use direct calls to the api.
 */
export class EmployeeDocsDataSource extends CustomDataSource {
  user_id = 0;

  // record sorting and direction
  sort_by = 'name';
  sort_order = 'asc';

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI(
      'folders/user/' + this.user_id,
      resetOffset,
      () => {},
      { eager_load: true }
    );
  }
}
